import { Chip, Grid, Link, Typography } from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import dayjs from "dayjs";
import { MouseEvent, ReactNode, useContext } from "react";
import { useHistory } from "react-router";
import {
  CARRIER_LABELS,
  Carrier,
  IssueSeverity,
  PackingSlipType,
  SHIPMENT_STATE_LABELS,
  ShipmentState,
  ShipmentType,
} from "types/ShippingTypes";
import { AccessEnum } from "types/users/UserTypes";

/** The header bar every Shipment page shares (same look as the Content
 *  pages). */
export const ShipmentPage: React.FC<{
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}> = ({ title, actions, children }) => (
  <Grid container>
    <Grid item xs={12}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          paddingRight: "10px",
          justifyContent: "space-between",
          background: "var(--header)",
          color: "#fff",
          height: "6vh",
          paddingTop: "20px",
        }}
      >
        <Typography
          style={{
            textTransform: "uppercase",
            color: "#fff",
            paddingLeft: "1rem",
          }}
          fontWeight={"bold"}
          variant={"h6"}
        >
          &nbsp;{title}
        </Typography>
        {actions}
      </div>
    </Grid>
    <Grid container>
      <Grid item xs={12} sx={{ padding: "1.5rem" }}>
        {children}
      </Grid>
    </Grid>
  </Grid>
);

/** An in-app link: a real href (so middle-click opens a tab) that navigates
 *  through the router on a plain click. */
export const AppLink: React.FC<{ to: string; children: ReactNode }> = ({
  to,
  children,
}) => {
  const history = useHistory();
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey)
      return;
    event.preventDefault();
    event.stopPropagation();
    history.push(to);
  };
  return (
    <Link href={to} onClick={onClick}>
      {children}
    </Link>
  );
};

/** Prices, settings, Mark as sent, Mark OK and the own-delivery cost are
 *  MANAGER actions on the backend; the buttons are hidden below that. */
export const useIsManager = (): boolean => {
  const { user } = useContext(AppStateContext);
  return (user?.accessLevel ?? 0) >= AccessEnum.MANAGER;
};

export const errorText = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : "Request failed";

export const formatDateTime = (value: string | null | undefined): string =>
  value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "";

export const formatDate = (value: string | null | undefined): string =>
  value ? dayjs(value).format("DD/MM/YYYY") : "";

export const formatKr = (value: number | null | undefined): string =>
  value == null ? "" : `${value.toFixed(2)} kr`;

export const formatKg = (grams: number | null | undefined): string =>
  grams == null ? "" : `${(grams / 1000).toFixed(2)} kg`;

export const carrierName = (carrier: string | null | undefined): string =>
  carrier ? CARRIER_LABELS[carrier as Carrier] ?? carrier.toUpperCase() : "";

const STATE_COLOURS: Record<
  ShipmentState,
  "default" | "primary" | "secondary" | "success" | "warning" | "error" | "info"
> = {
  booked: "warning",
  sent: "info",
  delivered: "success",
  ready: "warning",
  collected: "success",
  handed: "success",
  cancelled: "default",
};

export const ShipmentStateChip: React.FC<{ state: ShipmentState }> = ({
  state,
}) => (
  <Chip
    size="small"
    color={STATE_COLOURS[state] ?? "default"}
    label={SHIPMENT_STATE_LABELS[state] ?? state}
  />
);

const SEVERITY_COLOURS: Record<IssueSeverity, "error" | "warning" | "default"> =
  {
    high: "error",
    medium: "warning",
    low: "default",
  };

export const SeverityChip: React.FC<{ severity: IssueSeverity }> = ({
  severity,
}) => (
  <Chip
    size="small"
    color={SEVERITY_COLOURS[severity] ?? "default"}
    label={severity}
    sx={{ textTransform: "capitalize" }}
  />
);

export const shipmentKindText = (shipment: ShipmentType): string => {
  if (shipment.kind === "pickup") return "Pickup";
  if (shipment.kind === "own_delivery") return "Own delivery";
  return carrierName(shipment.carrier) || "Carrier";
};

const LABEL_EXTENSIONS: Record<string, { ext: string; mime: string }> = {
  zpl: { ext: "zpl", mime: "application/octet-stream" },
  png: { ext: "png", mime: "image/png" },
  pdf: { ext: "pdf", mime: "application/pdf" },
};

/** Saves the stored label (base64 from GET /shipments/<id>/label). */
export const downloadLabel = (
  base64: string,
  fileFormat: string,
  name: string
) => {
  const type = LABEL_EXTENSIONS[fileFormat] ?? {
    ext: fileFormat || "bin",
    mime: "application/octet-stream",
  };
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: type.mime }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}.${type.ext}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** The packing slip goes to the laser printer from the browser: order no,
 *  customer and the lines of this shipment only (a split prints its own
 *  part). Returns false when the browser blocked the print window. */
export const printPackingSlip = (slip: PackingSlipType): boolean => {
  const win = window.open("", "_blank", "width=800,height=900");
  if (!win) return false;
  const address = slip.shippingAddress;
  const person = [address?.firstName, address?.lastName]
    .filter(Boolean)
    .join(" ");
  const how =
    slip.kind === "pickup"
      ? "Pickup"
      : slip.kind === "own_delivery"
      ? "Own delivery"
      : carrierName(slip.carrier);
  const rows = slip.lines
    .map(
      (line) =>
        `<tr><td>${escapeHtml(line.name)}</td><td class="n">${escapeHtml(
          line.quantity
        )}</td><td class="n">${escapeHtml(line.ordered ?? "")}</td></tr>`
    )
    .join("");
  win.document.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>Packing slip ${escapeHtml(
    slip.orderNo
  )}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 32px; color: #000; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .meta { margin: 0 0 24px; font-size: 14px; }
  .meta div { margin: 2px 0; }
  .address { margin: 0 0 24px; font-size: 14px; line-height: 1.4; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  th, td { border-bottom: 1px solid #999; padding: 6px 4px; text-align: left; }
  td.n, th.n { text-align: right; width: 90px; }
</style></head><body>
<h1>Packing slip - order ${escapeHtml(slip.orderNo)}</h1>
<div class="meta">
  <div>Date: ${escapeHtml(formatDate(slip.date))}</div>
  ${slip.poNumber ? `<div>PO number: ${escapeHtml(slip.poNumber)}</div>` : ""}
  <div>Shipping: ${escapeHtml(how)}${
    slip.trackingNo ? ` (${escapeHtml(slip.trackingNo)})` : ""
  }</div>
</div>
<div class="address">
  <strong>${escapeHtml(slip.customer)}</strong><br>
  ${address?.company ? `${escapeHtml(address.company)}<br>` : ""}
  ${person ? `${escapeHtml(person)}<br>` : ""}
  ${escapeHtml(address?.street)}<br>
  ${escapeHtml(address?.postalCode)} ${escapeHtml(address?.city)}<br>
  ${escapeHtml(address?.countryCode)}
</div>
<table>
  <thead><tr><th>Item</th><th class="n">In this shipment</th><th class="n">Ordered</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
</body></html>`);
  win.document.close();
  win.focus();
  win.print();
  return true;
};
